import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  ClipboardCheck, Plus, RefreshCw, Loader2, FileSpreadsheet, Lock, Unlock, AlertTriangle, TrendingUp, TrendingDown, ListChecks, Search, Package, Trash2,
} from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtMoney = (n) => {
  const x = Number(n) || 0;
  return '₹' + x.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function CreateSessionDialog({ open, onClose, onCreated }) {
  const [warehouse, setWarehouse] = useState('Main Warehouse');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const handleCreate = async () => {
    if (!warehouse.trim()) { toast.error('Warehouse is required'); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/stock-audit/sessions`,
        { warehouse: warehouse.trim(), notes: notes.trim() },
        { headers: { Authorization: 'Bearer ' + token } },
      );
      toast.success(`Audit session ${res.data.audit_number} created`);
      onCreated(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create session');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="create-audit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-violet-700">
            <ClipboardCheck size={18} /> Start New Audit Session
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-semibold uppercase text-slate-500">Warehouse</label>
            <Input
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
              placeholder="Main Warehouse"
              data-testid="audit-warehouse-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-slate-500">Notes (optional)</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Q2 quarterly cycle count"
              data-testid="audit-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={busy}
            className="bg-violet-600 hover:bg-violet-700 text-white gap-1"
            data-testid="audit-create-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Create &amp; Open
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotAuditedDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');

  const fetchList = useCallback(async (q) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/stock-audit/not-audited`, {
        headers: { Authorization: 'Bearer ' + token },
        params: q ? { search: q } : {},
      });
      setProducts(res.data.products || []);
    } catch (e) {
      toast.error('Failed to load list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearch('');
      fetchList('');
    }
  }, [open, fetchList]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchList(search), 350);
    return () => clearTimeout(t);
  }, [search, open, fetchList]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl" data-testid="not-audited-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <ListChecks size={18} /> Products Not Yet Audited
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-500">
            Products that are currently <strong>in inventory</strong> (have stock-master flag or any batch) but have never been part of a submitted Stock Audit. Master-catalogue SKUs that have never been inwarded into the warehouse are not counted here. Once a product appears in any submitted session it drops off this list automatically.
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, SKU, barcode, brand or category"
              className="pl-8 h-9"
              data-testid="not-audited-search"
            />
          </div>
          <div className="border rounded max-h-[480px] overflow-auto">
            <table className="w-full text-sm" data-testid="not-audited-table">
              <thead className="sticky top-0 bg-slate-100 border-b text-xs uppercase text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Product ID</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Brand</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-right px-3 py-2">MRP</th>
                  <th className="text-right px-3 py-2">Current Stock</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading && (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  </td></tr>
                )}
                {!loading && products.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-12 text-emerald-700">
                    <Package className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                    <p className="font-medium">All products have been audited at least once</p>
                  </td></tr>
                )}
                {!loading && products.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50" data-testid={`not-audited-row-${p.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{p.product_id || '—'}</td>
                    <td className="px-3 py-2">{p.name}</td>
                    <td className="px-3 py-2 text-slate-600">{p.brand || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{p.category || '—'}</td>
                    <td className="px-3 py-2 text-right">{p.mrp ? '₹' + Number(p.mrp).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold">{p.current_stock || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-slate-500 text-right" data-testid="not-audited-count">
            Showing {products.length} product{products.length === 1 ? '' : 's'}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function StockAudit() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showNotAudited, setShowNotAudited] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  // Sessions that are currently being deleted (id-keyed) and the row queued
  // for confirmation. Open-only — Locked sessions are intentionally non-deletable
  // to preserve the inventory audit trail (variance is already applied).
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(
        `${API}/stock-audit/sessions/${pendingDelete.id}`,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      toast.success(`Deleted ${res.data.audit_number} (${res.data.entries_deleted} line${res.data.entries_deleted === 1 ? '' : 's'})`);
      // Optimistic UI update — drop the row immediately, also refetch dashboard tiles.
      setSessions(prev => prev.filter(s => s.id !== pendingDelete.id));
      fetchAll();
      setPendingDelete(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Delete failed';
      toast.error(typeof msg === 'string' ? msg : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // Generic XLSX downloader — pulls a binary blob from `path`, names it `filename`,
  // and clicks an invisible <a> to trigger the browser save dialog.
  const downloadXlsx = async (path, filename) => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}${path}`, {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAll = async () => {
    setExportingAll(true);
    try {
      await downloadXlsx('/stock-audit/export-all.xlsx', `stock_audit_all_lines_${new Date().toISOString().slice(0,10)}.xlsx`);
      toast.success('All audited lines exported');
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExportingAll(false);
    }
  };

  const handleExportSession = async (session) => {
    try {
      await downloadXlsx(`/stock-audit/sessions/${session.id}/variance-report.xlsx`, `stock_audit_${session.audit_number}.xlsx`);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [sRes, dRes] = await Promise.all([
        axios.get(`${API}/stock-audit/sessions`, { headers: { Authorization: 'Bearer ' + token } }),
        axios.get(`${API}/stock-audit/dashboard`, { headers: { Authorization: 'Bearer ' + token } }),
      ]);
      setSessions(sRes.data.sessions || []);
      setDashboard(dRes.data);
    } catch (e) {
      toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreated = (session) => {
    setCreating(false);
    fetchAll();
    navigate(`/stock-audit/sessions/${session.id}`);
  };

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="stock-audit-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-800 flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-violet-600" />
            Stock Audit
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Barcode-driven physical verification · Variance tracking · Locked audit trail
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={fetchAll}
            disabled={loading}
            data-testid="audit-refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button
            variant="outline"
            onClick={handleExportAll}
            disabled={exportingAll || sessions.length === 0}
            className="gap-1 border-violet-300 text-violet-700"
            data-testid="audit-export-all-btn"
            title="Download every audited line item across all sessions in one XLSX"
          >
            {exportingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            Export All
          </Button>
          <CsvDownloadButton onDownload={handleExportAll} testId="audit-export-all-csv" size="default" disabled={sessions.length === 0} />
          <Button
            onClick={() => setCreating(true)}
            className="gap-1 bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="audit-new-session-btn"
          >
            <Plus size={16} /> New Audit Session
          </Button>
        </div>
      </div>

      {/* Dashboard tiles */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="audit-dashboard-tiles">
          <Card className="border"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Open Sessions</div>
            <div className="text-2xl font-bold text-violet-700 mt-1" data-testid="tile-open">{dashboard.open_sessions}</div>
          </CardContent></Card>
          <Card className="border"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Submitted</div>
            <div className="text-2xl font-bold text-slate-800 mt-1" data-testid="tile-submitted">{dashboard.submitted_sessions}</div>
          </CardContent></Card>
          <Card className="border"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Lines Audited</div>
            <div className="text-2xl font-bold text-blue-700 mt-1" data-testid="tile-lines">{dashboard.audited_lines}</div>
          </CardContent></Card>
          <Card
            className="border border-amber-300 bg-amber-50 cursor-pointer hover:bg-amber-100 hover:shadow transition-all"
            onClick={() => setShowNotAudited(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowNotAudited(true); }}
            data-testid="tile-not-audited-card"
          >
            <CardContent className="p-3">
              <div className="text-[10px] uppercase text-amber-700 font-semibold flex items-center gap-1"><ListChecks size={11} /> Not Audited</div>
              <div className="text-2xl font-bold text-amber-700 mt-1" data-testid="tile-not-audited">{dashboard.not_audited_count}</div>
              <div className="text-[10px] text-amber-600 mt-0.5">in inventory · click to view</div>
            </CardContent>
          </Card>
          <Card className="border border-emerald-200 bg-emerald-50"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-emerald-700 font-semibold flex items-center gap-1"><TrendingUp size={11} /> Excess</div>
            <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="tile-excess">{dashboard.excess_qty}</div>
          </CardContent></Card>
          <Card className="border border-red-200 bg-red-50"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-red-700 font-semibold flex items-center gap-1"><TrendingDown size={11} /> Shortage</div>
            <div className="text-2xl font-bold text-red-700 mt-1" data-testid="tile-shortage">{dashboard.shortage_qty}</div>
          </CardContent></Card>
          <Card className="border"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Excess Value</div>
            <div className="text-lg font-bold text-emerald-700 mt-1">{fmtMoney(dashboard.excess_value)}</div>
          </CardContent></Card>
          <Card className="border"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Shortage Value</div>
            <div className="text-lg font-bold text-red-700 mt-1">{fmtMoney(dashboard.shortage_value)}</div>
          </CardContent></Card>
        </div>
      )}

      {/* Sessions table */}
      <Card className="border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-sessions-table">
            <thead className="bg-slate-100 border-b text-xs uppercase text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Audit #</th>
                <th className="text-left px-4 py-2 font-semibold">Warehouse</th>
                <th className="text-left px-4 py-2 font-semibold">Created</th>
                <th className="text-left px-4 py-2 font-semibold">Created By</th>
                <th className="text-center px-4 py-2 font-semibold">Status</th>
                <th className="text-right px-4 py-2 font-semibold">Lines</th>
                <th className="text-right px-4 py-2 font-semibold">Excess Qty</th>
                <th className="text-right px-4 py-2 font-semibold">Shortage Qty</th>
                <th className="text-right px-4 py-2 font-semibold">Variance ₹</th>
                <th className="text-center px-4 py-2 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {!loading && sessions.length === 0 && (
                <tr><td colSpan={10} className="text-center py-12 text-slate-500">
                  <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="font-medium">No audit sessions yet.</p>
                  <p className="text-xs mt-1">Click "New Audit Session" to start your first verification.</p>
                </td></tr>
              )}
              {sessions.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50" data-testid={`session-row-${s.id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{s.audit_number}</td>
                  <td className="px-4 py-2">{s.warehouse}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {(s.created_at || '').replace('T', ' ').split('.')[0]}
                  </td>
                  <td className="px-4 py-2 text-xs">{s.created_by_name}</td>
                  <td className="px-4 py-2 text-center">
                    {s.status === 'submitted' ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1"><Lock size={10} /> Locked</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1"><Unlock size={10} /> Open</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold">{s.totals?.entries_count || 0}</td>
                  <td className="px-4 py-2 text-right text-emerald-700">{s.totals?.excess_qty || 0}</td>
                  <td className="px-4 py-2 text-right text-red-700">{s.totals?.shortage_qty || 0}</td>
                  <td className="px-4 py-2 text-right">{fmtMoney(s.totals?.variance_value)}</td>
                  <td className="px-4 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/stock-audit/sessions/${s.id}`)}
                        className="gap-1"
                        data-testid={`open-session-${s.id}`}
                      >
                        Open
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => handleExportSession(s)}
                        className="h-8 w-8 border-violet-300 text-violet-700 hover:bg-violet-50"
                        title="Download line-item XLSX for this session"
                        data-testid={`export-session-${s.id}`}
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5" />
                      </Button>
                      <CsvDownloadButton onDownload={() => handleExportSession(s)} testId={`export-session-csv-${s.id}`} className="h-8 px-2 text-xs" />
                      {s.status !== 'submitted' && (
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => setPendingDelete(s)}
                          className="h-8 w-8 border-red-300 text-red-600 hover:bg-red-50"
                          title="Delete this Open session"
                          data-testid={`delete-session-${s.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <CreateSessionDialog open={creating} onClose={() => setCreating(false)} onCreated={handleCreated} />
      <NotAuditedDialog open={showNotAudited} onClose={() => setShowNotAudited(false)} />

      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && !deleting && setPendingDelete(null)}>
        <DialogContent data-testid="confirm-delete-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Delete Audit Session
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>
              You are about to delete <strong className="font-mono">{pendingDelete?.audit_number}</strong>
              {' '}({pendingDelete?.warehouse}) along with{' '}
              <strong>{pendingDelete?.totals?.entries_count || 0}</strong> scanned line
              {(pendingDelete?.totals?.entries_count || 0) === 1 ? '' : 's'}.
            </p>
            <p className="text-xs text-slate-500">
              This action is permanent. Only Open sessions can be deleted — Locked
              sessions are immutable to preserve the inventory audit trail.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={deleting} onClick={() => setPendingDelete(null)} data-testid="cancel-delete-audit">
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white gap-1"
              disabled={deleting}
              onClick={confirmDelete}
              data-testid="confirm-delete-audit"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Delete Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
